import { createBrowserRouter, Navigate } from "react-router-dom";
import { RootLayout } from "@/layouts/RootLayout";
import { ProtectedLayout } from "@/layouts/ProtectedLayout";
import { DefaultContentWrapper } from "@/layouts/DefaultContentWrapper";
import { AssessmentShell } from "@/layouts/AssessmentShell";
import { ReportPhaseWrapper } from "@/layouts/ReportPhaseWrapper";
import { EvidenceFindingsWrapper } from "@/layouts/EvidenceFindingsWrapper";
import { InvestorReportLayout } from "@/layouts/InvestorReportLayout";
import { RootRedirect } from "@/components/RootRedirect";

import { lazy, Suspense } from "react";

const SignInPage = lazy(() => import("@/pages/SignInPage"));
const SignUpPage = lazy(() => import("@/pages/SignUpPage"));
const AssessmentsPage = lazy(
  () => import("@/pages/assessments/AssessmentsPage"),
);
const NewAssessmentPage = lazy(
  () => import("@/pages/assessments/NewAssessmentPage"),
);
const AssessmentDetailPage = lazy(
  () => import("@/pages/assessments/AssessmentDetailPage"),
);
const QuestionsPage = lazy(() => import("@/pages/assessments/QuestionsPage"));
const ExecutiveSummaryPage = lazy(
  () => import("@/pages/assessments/ExecutiveSummaryPage"),
);
const RiskMatrixPage = lazy(() => import("@/pages/assessments/RiskMatrixPage"));
const DimensionPage = lazy(() => import("@/pages/assessments/DimensionPage"));
const DefensibilityPage = lazy(
  () => import("@/pages/assessments/DefensibilityPage"),
);
const AiMaturityPage = lazy(() => import("@/pages/assessments/AiMaturityPage"));
const AnalysisCodeScanPage = lazy(
  () => import("@/pages/assessments/AnalysisCodeScanPage"),
);
const CodeScanFindingsPage = lazy(
  () => import("@/pages/assessments/CodeScanFindingsPage"),
);
const InfraScanFindingsPage = lazy(
  () => import("@/pages/assessments/InfraScanFindingsPage"),
);
const AnalysisInfraScanPage = lazy(
  () => import("@/pages/assessments/AnalysisInfraScanPage"),
);
const IntegrationsPage = lazy(
  () => import("@/pages/settings/IntegrationsPage"),
);
const ConsentPage = lazy(() => import("@/pages/connect-agent/ConsentPage"));
const InvitationsPage = lazy(() => import("@/pages/settings/InvitationsPage"));
const InvestorPortfolioPage = lazy(
  () => import("@/pages/investor/InvestorPortfolioPage"),
);
const InvitationAcceptPage = lazy(() => import("@/pages/InvitationAcceptPage"));
const JoinPage = lazy(() => import("@/pages/JoinPage"));
const AssessmentRequestAcceptPage = lazy(
  () => import("@/pages/AssessmentRequestAcceptPage"),
);
const PipelinePage = lazy(() => import("@/pages/investor/PipelinePage"));
const DealStatusPage = lazy(() => import("@/pages/investor/DealStatusPage"));
const CompanySetupPage = lazy(() => import("@/pages/CompanySetupPage"));
const ConnectAnAgentHelpPage = lazy(
  () => import("@/pages/help/ConnectAnAgent"),
);
const ErrorPage = lazy(() => import("@/pages/ErrorPage"));
const DesignSystemPage = lazy(() => import("@/pages/DesignSystemPage"));

/**
 * Whether to expose the in-app /design-system reference route.
 * Always on in dev; opt-in in staging/prod via VITE_ENABLE_DESIGN_SYSTEM=true.
 */
const DESIGN_SYSTEM_ENABLED =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_DESIGN_SYSTEM === "true";

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

// Shared children for any route that renders the analysis/report-phase
// sidebar layout. Used by both the CTO assessment view (under
// ReportPhaseWrapper) and the investor portfolio view (under
// InvestorReportLayout).
const analysisPhaseChildren = [
  {
    index: true,
    element: <Navigate to="executive-summary" replace />,
  },
  {
    path: "executive-summary",
    element: (
      <SuspenseWrapper>
        <ExecutiveSummaryPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "risk-matrix",
    element: (
      <SuspenseWrapper>
        <RiskMatrixPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "defensibility",
    element: (
      <SuspenseWrapper>
        <DefensibilityPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "ai-maturity",
    element: (
      <SuspenseWrapper>
        <AiMaturityPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "code-scan",
    element: (
      <SuspenseWrapper>
        <AnalysisCodeScanPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "code-scan/findings",
    element: (
      <SuspenseWrapper>
        <CodeScanFindingsPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "infra-scan",
    element: (
      <SuspenseWrapper>
        <AnalysisInfraScanPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "infra-scan/findings",
    element: (
      <SuspenseWrapper>
        <InfraScanFindingsPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: ":dimension",
    element: (
      <SuspenseWrapper>
        <DimensionPage />
      </SuspenseWrapper>
    ),
  },
];

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: (
      <SuspenseWrapper>
        <ErrorPage />
      </SuspenseWrapper>
    ),
    children: [
      // Public routes
      { path: "/", element: <RootRedirect /> },
      ...(DESIGN_SYSTEM_ENABLED
        ? [
            {
              path: "/design-system",
              element: (
                <SuspenseWrapper>
                  <DesignSystemPage />
                </SuspenseWrapper>
              ),
            },
          ]
        : []),
      {
        path: "/sign-in/*",
        element: (
          <SuspenseWrapper>
            <SignInPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/sign-up/*",
        element: (
          <SuspenseWrapper>
            <SignUpPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/invitations/accept",
        element: (
          <SuspenseWrapper>
            <InvitationAcceptPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/join/:inviteCode",
        element: (
          <SuspenseWrapper>
            <JoinPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/assessment-requests/:token",
        element: (
          <SuspenseWrapper>
            <AssessmentRequestAcceptPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/company-setup",
        element: (
          <SuspenseWrapper>
            <CompanySetupPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/help/connect-an-agent",
        element: (
          <SuspenseWrapper>
            <ConnectAnAgentHelpPage />
          </SuspenseWrapper>
        ),
      },

      // Protected routes
      {
        element: <ProtectedLayout />,
        children: [
          // Non-assessment pages: centered content with max-w-7xl
          {
            element: <DefaultContentWrapper />,
            children: [
              {
                path: "/assessments",
                element: (
                  <SuspenseWrapper>
                    <AssessmentsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "/assessments/new",
                element: (
                  <SuspenseWrapper>
                    <NewAssessmentPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "/settings/integrations",
                element: (
                  <SuspenseWrapper>
                    <IntegrationsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "/connect-agent/consent",
                element: (
                  <SuspenseWrapper>
                    <ConsentPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "/settings/invitations",
                element: (
                  <SuspenseWrapper>
                    <InvitationsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "/portfolio",
                element: (
                  <SuspenseWrapper>
                    <InvestorPortfolioPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "/pipeline",
                element: (
                  <SuspenseWrapper>
                    <PipelinePage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "/pipeline/:requestId",
                element: (
                  <SuspenseWrapper>
                    <DealStatusPage />
                  </SuspenseWrapper>
                ),
              },
              // Email deep-links (accept/decline/expire notifications) point at
              // /portfolio/requests — keep it alive as a redirect so old links
              // never 404 (sv-0f2).
              {
                path: "/portfolio/requests",
                element: <Navigate to="/pipeline" replace />,
              },
            ],
          },

          // Assessment detail pages: AssessmentShell provides breadcrumb + PhaseBar
          {
            element: <AssessmentShell />,
            children: [
              {
                path: "/assessments/:id",
                element: (
                  <SuspenseWrapper>
                    <AssessmentDetailPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "/assessments/:id/questions",
                element: (
                  <SuspenseWrapper>
                    <QuestionsPage />
                  </SuspenseWrapper>
                ),
              },
              // STA-311: Standalone findings routes live in evidence-collection chrome
              // (AssessmentShell) so a CTO clicking "View Findings" during the
              // Collecting Evidence phase isn't yanked into the report-phase chrome
              // they haven't reached yet. The /analysis/.../findings routes below
              // are still used by the report-phase DimensionNav.
              //
              // STA-312 / sv-3wu: {infra,code}-scan/findings are nested under
              // EvidenceFindingsWrapper so EvidenceTrackSidebar renders alongside
              // the triage page with the owning track preselected and a
              // "Findings" sub-item indicated.
              {
                path: "/assessments/:id/infra-scan/findings",
                element: (
                  <EvidenceFindingsWrapper activeTrack="infrastructure" />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <SuspenseWrapper>
                        <InfraScanFindingsPage />
                      </SuspenseWrapper>
                    ),
                  },
                ],
              },
              {
                path: "/assessments/:id/code-scan/findings",
                element: <EvidenceFindingsWrapper activeTrack="codeScan" />,
                children: [
                  {
                    index: true,
                    element: (
                      <SuspenseWrapper>
                        <CodeScanFindingsPage />
                      </SuspenseWrapper>
                    ),
                  },
                ],
              },
              // Report phase: DimensionNav sidebar via ReportPhaseWrapper
              {
                path: "/assessments/:id/analysis",
                element: <ReportPhaseWrapper />,
                children: analysisPhaseChildren,
              },
            ],
          },

          // Investor report pages: same sidebar layout, different context
          // Redirect old standalone investor code-scan/findings URL to new location under analysis
          {
            path: "/portfolio/:tenantId/assessments/:assessmentId/code-scan/findings",
            element: <Navigate to="../analysis/code-scan/findings" replace />,
          },
          {
            path: "/portfolio/:tenantId/assessments/:assessmentId/analysis",
            element: <InvestorReportLayout />,
            children: analysisPhaseChildren,
          },

          // Redirect old investor report URL to new analysis URL
          {
            path: "/portfolio/:tenantId/assessments/:assessmentId",
            element: <RedirectToAnalysis />,
          },
        ],
      },
    ],
  },
]);

function RedirectToAnalysis() {
  return <Navigate to="analysis" replace />;
}
